import {
  Box,
  BoxProps,
  ButtonBase,
  InputAdornment,
  OutlinedInput as MuiOutlinedInput,
  OutlinedInputProps as MuiOutlinedInputProps,
  buttonBaseClasses,
  outlinedInputClasses,
  styled,
  useControlled,
} from '@mui/material';
import React from 'react';
import clsx from 'clsx';
import Add from '../icons/Add';
import ExpandLess from '../icons/ExpandLess';
import Remove from '../icons/Remove';
import { Secondary } from '../color';
import { Spacing1, Spacing2, Spacing3 } from '../spacing';
import { Corners } from '../corners/tokens';
import { ButtonGroup } from '../buttonGroup';
import { IconButton } from '../iconButton';
import { numberInputClasses } from './types';

const NumberInputRoot = styled(MuiOutlinedInput)<MuiOutlinedInputProps>(({ size }) => ({
  paddingRight: size === 'small'
    ? 0
    : Spacing2,
  [`&.${outlinedInputClasses.sizeSmall} .${outlinedInputClasses.input}`]: {
    paddingLeft: Spacing3,
  },
  [`&.${numberInputClasses.controlsExternal}`]: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  [`.${outlinedInputClasses.input},
  .${outlinedInputClasses.input}::-webkit-outer-spin-button,
  .${outlinedInputClasses.input}::-webkit-inner-spin-button
  `]: {
    '-webkit-appearance': 'none',
    '-moz-appearance': 'textfield',
  },
}));

interface ControlButtonProps {
  size?: NumberInputProps['size'];
}

const ControlButton = styled(ButtonBase)<ControlButtonProps>(({ size }) => ({
  width: 24,
  height: 14,
  backgroundColor: Secondary.container.main,
  display: 'flex',
  borderRadius: size === 'small'
    ? 0
    : `${Corners.large} ${Corners.large} 0px 0px`,
  [`&.${buttonBaseClasses.disabled}`]: {
    pointerEvents: 'auto',
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: Secondary.container.main,
    },
  },

  ':hover': {
    backgroundColor: Secondary.container.hover,
  },
  ':active': {
    backgroundColor: Secondary.container.pressed,
  },
  ':first-of-type': {
    marginBottom: Spacing1,
  },
  ':nth-of-type(2)': {
    transform: 'rotate(180deg)',
  },
}));

export interface NumberInputOwnProps {
  step?: number;
  decimals?: number;
  min?: number;
  max?: number;
  shiftMultiplier?: number;
  controls?: 'integrated' | 'external' | false;

  onChange?: (event: React.MouseEvent | React.ChangeEvent, value: number | typeof NaN) => void;
  value?: number;
  defaultValue?: number;
  slotProps?: MuiOutlinedInputProps['slotProps'] & {
    wrapper?: BoxProps;
    controlAdd?: ControlButtonProps;
    controlSub?: ControlButtonProps;
  }
}

export type NumberInputProps = NumberInputOwnProps & Omit<MuiOutlinedInputProps, 'onChange'>;

const getNextValue = (
  event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  op: 'add' | 'sub',
  value: number,
  multiplier = 10,
  step = 1,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
) => {
  const finalStep = event.shiftKey ? step * multiplier : step;

  let nextValue = value + (finalStep * (op === 'add' ? 1 : -1));

  if (op === 'add' && nextValue > max) {
    nextValue = max;
  } else if (op === 'sub' && nextValue < min) {
    nextValue = min;
  }

  return nextValue;
};

export const NumberInput = React.forwardRef(
  (inProps: NumberInputProps, ref: React.RefObject<unknown>) => {
    const {
      className,
      step = 1,
      shiftMultiplier = 10,
      decimals,
      min,
      max,
      value: valueProp,
      defaultValue: defaultValueProp,
      disabled,
      endAdornment,
      onChange,
      size,
      controls = 'integrated',
      slotProps,
      inputProps,
      ...rest
    } = inProps;

    const [value, setValue] = useControlled<number>({
      controlled: valueProp,
      default: defaultValueProp,
      name: 'NumberInput',
    });

    const [displayValue, setDisplayValue] = React.useState<string>(
      value && decimals
        ? value.toFixed(decimals)
        : String(value || ''),
    );

    const add = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const nextValue = value !== undefined
        ? getNextValue(event, 'add', value, shiftMultiplier, step, min, max)
        : min || 0;

      if (onChange) {
        onChange(event, nextValue);
      }

      if (decimals) {
        setDisplayValue(nextValue.toFixed(decimals));
      } else {
        setDisplayValue(String(nextValue));
      }

      setValue(nextValue);
    };

    const subtract = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const nextValue = value !== undefined
        ? getNextValue(event, 'sub', value, shiftMultiplier, step, min, max)
        : max || 0;

      if (onChange) {
        onChange(event, nextValue);
      }

      if (decimals) {
        setDisplayValue(nextValue.toFixed(decimals));
      } else {
        setDisplayValue(String(nextValue));
      }

      setValue(nextValue);
    };

    return (
      <Box
        {...slotProps?.wrapper}
        sx={{
          display: 'inline-flex',
          ...slotProps?.wrapper?.sx,
        }}
      >
        <NumberInputRoot
          ref={ref}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          type="number"
          {...rest}
          inputProps={{
            'aria-valuemin': min,
            'aria-valuemax': max,
            'aria-valuenow': value,
            ...inputProps,
          }}
          className={clsx(className, {
            [numberInputClasses.controlsExternal]: controls === 'external',
            [numberInputClasses.controlsIntegrated]: controls === 'integrated',
            [numberInputClasses.controlsNone]: controls === false,
          })}
          size={size}
          value={displayValue}
          onChange={(event) => {
            setDisplayValue(event.target.value);
            const parsedValue = parseFloat(event.target.value);
            if (onChange) {
              onChange(event, parsedValue);
            }

            if (!Number.isNaN(parsedValue)) {
              setValue(parsedValue);
            }
          }}
          endAdornment={(
            <>
              {endAdornment}

              {controls === 'integrated' && (
                <InputAdornment
                  position="end"
                  sx={{
                    flexDirection: 'column',
                  }}
                >
                  <ControlButton
                    disabled={disabled || (max !== undefined && value === max)}
                    onClick={add}
                    size={size}
                    aria-label="Add"
                    {...slotProps?.controlAdd}
                  >
                    <ExpandLess />
                  </ControlButton>

                  <ControlButton
                    disabled={disabled || (min !== undefined && value === min)}
                    onClick={subtract}
                    size={size}
                    aria-label="Subtract"
                    {...slotProps?.controlSub}
                  >
                    <ExpandLess />
                  </ControlButton>
                </InputAdornment>
              )}
            </>
          )}
        />

        {controls === 'external' && (
          <ButtonGroup
            size={size}
            colorVariant="tonal"
            sx={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          >
            <IconButton
              aria-label="Add"
              disabled={disabled || (max !== undefined && value === max)}
              {...slotProps?.controlAdd}
              sx={{
                borderRadius: 0,
              }}
            >
              <Add />
            </IconButton>

            <IconButton
              aria-label="Subtract"
              disabled={disabled || (min !== undefined && value === min)}
              {...slotProps?.controlSub}
            >
              <Remove />
            </IconButton>
          </ButtonGroup>
        )}
      </Box>
    );
  },
);
